import React from 'react';
import {
  Box,
  TextField,
  FormControlLabel,
  Switch,
  Typography,
  IconButton,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, ExpandMore } from '@mui/icons-material';

import { MethodEditorProps } from '../types';

export const SearchNonprofitsEditor: React.FC<MethodEditorProps> = ({ payload, onChange }) => {
  const normalizedPayload = {
    outputPath: 'searchResults',
    searchTerm: '',
    acceptsDonations: true,
    causes: [],
    countryCode: '',
    stateCode: '',
    donationProvider: '',
    excludeDonationProviders: [],
    includeApproved: true,
    limit: 10,
    offset: 0,
    waitForCompletion: true,
    ...payload,
  };

  const handleArrayChange = (field: string, values: string[]) => {
    onChange({ ...normalizedPayload, [field]: values });
  };

  const addArrayItem = (field: string) => {
    const currentArray = normalizedPayload[field] || [];
    handleArrayChange(field, [...currentArray, '']);
  };

  const updateArrayItem = (field: string, index: number, value: string) => {
    const currentArray = [...(normalizedPayload[field] || [])];
    currentArray[index] = value;
    handleArrayChange(field, currentArray);
  };

  const removeArrayItem = (field: string, index: number) => {
    const currentArray = normalizedPayload[field] || [];
    handleArrayChange(
      field,

      currentArray.filter((_item: unknown, i: number) => i !== index)
    );
  };

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="body2">
          <strong>Search Nonprofits:</strong> Use the impactAiNonprofitSearch GraphQL query to find
          nonprofits based on various criteria. Results will be stored at the specified output path.
        </Typography>
      </Alert>

      {/* Required Fields */}
      <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
        Required Settings
      </Typography>

      <TextField
        fullWidth
        value={normalizedPayload.outputPath}
        onChange={(e) => onChange({ ...normalizedPayload, outputPath: e.target.value })}
        label="Output Path"
        placeholder="searchResults"
        helperText="Context path where search results will be stored"
        margin="normal"
        required
      />

      {/* Search Parameters */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6">Search Parameters</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box>
            <TextField
              fullWidth
              value={normalizedPayload.searchTerm || ''}
              onChange={(e) => onChange({ ...normalizedPayload, searchTerm: e.target.value })}
              label="Search Term"
              placeholder="education, healthcare, environment..."
              helperText="Text to search for in nonprofit names and descriptions"
              margin="normal"
            />

            <TextField
              fullWidth
              value={normalizedPayload.countryCode || ''}
              onChange={(e) => onChange({ ...normalizedPayload, countryCode: e.target.value })}
              label="Country Code"
              placeholder="US, CA, GB..."
              helperText="ISO country code to filter by"
              margin="normal"
            />

            <TextField
              fullWidth
              value={normalizedPayload.stateCode || ''}
              onChange={(e) => onChange({ ...normalizedPayload, stateCode: e.target.value })}
              label="State Code"
              placeholder="CA, NY, TX..."
              helperText="State/province code to filter by"
              margin="normal"
            />

            <TextField
              fullWidth
              value={normalizedPayload.donationProvider || ''}
              onChange={(e) => onChange({ ...normalizedPayload, donationProvider: e.target.value })}
              label="Donation Provider"
              placeholder="paypal, stripe..."
              helperText="Filter by specific donation provider"
              margin="normal"
            />
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Array Fields */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6">Advanced Filters</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box>
            {/* Causes */}
            <Box sx={{ mb: 2 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="subtitle2">Causes</Typography>
                <IconButton size="small" onClick={() => addArrayItem('causes')}>
                  <AddIcon />
                </IconButton>
              </Box>
              {normalizedPayload.causes?.map((cause: string, index: number) => (
                // TODO@theronburger Do this better its breaking things
                // eslint-disable-next-line react/no-array-index-key
                <Box key={index} display="flex" alignItems="center" gap={1} mb={1}>
                  <TextField
                    fullWidth
                    size="small"
                    value={cause}
                    onChange={(e) => updateArrayItem('causes', index, e.target.value)}
                    placeholder="education, healthcare, environment..."
                    helperText={index === 0 ? 'Filter by cause categories' : ''}
                  />
                  <IconButton
                    size="small"
                    onClick={() => removeArrayItem('causes', index)}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              ))}
              {(!normalizedPayload.causes || normalizedPayload.causes.length === 0) && (
                <Typography variant="body2" color="text.secondary">
                  No causes specified. Click + to add cause filters.
                </Typography>
              )}
            </Box>

            {/* Exclude Donation Providers */}
            <Box sx={{ mb: 2 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="subtitle2">Exclude Donation Providers</Typography>
                <IconButton size="small" onClick={() => addArrayItem('excludeDonationProviders')}>
                  <AddIcon />
                </IconButton>
              </Box>
              {normalizedPayload.excludeDonationProviders?.map(
                (provider: string, index: number) => (
                  // TODO@theronburger Do this better its breaking things
                  // eslint-disable-next-line react/no-array-index-key
                  <Box key={index} display="flex" alignItems="center" gap={1} mb={1}>
                    <TextField
                      fullWidth
                      size="small"
                      value={provider}
                      onChange={(e) =>
                        updateArrayItem('excludeDonationProviders', index, e.target.value)
                      }
                      placeholder="paypal, stripe..."
                      helperText={index === 0 ? 'Exclude nonprofits using these providers' : ''}
                    />
                    <IconButton
                      size="small"
                      onClick={() => removeArrayItem('excludeDonationProviders', index)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                )
              )}
              {(!normalizedPayload.excludeDonationProviders ||
                normalizedPayload.excludeDonationProviders.length === 0) && (
                <Typography variant="body2" color="text.secondary">
                  No providers excluded. Click + to exclude specific donation providers.
                </Typography>
              )}
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Boolean Options */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6">Options</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={normalizedPayload.acceptsDonations}
                  onChange={(e) =>
                    onChange({ ...normalizedPayload, acceptsDonations: e.target.checked })
                  }
                />
              }
              label="Accepts Donations"
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Only include nonprofits that accept donations
            </Typography>

            <FormControlLabel
              control={
                <Switch
                  checked={normalizedPayload.includeApproved}
                  onChange={(e) =>
                    onChange({ ...normalizedPayload, includeApproved: e.target.checked })
                  }
                />
              }
              label="Include Approved Only"
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Only include pre-approved nonprofits
            </Typography>

            <FormControlLabel
              control={
                <Switch
                  checked={normalizedPayload.waitForCompletion}
                  onChange={(e) =>
                    onChange({ ...normalizedPayload, waitForCompletion: e.target.checked })
                  }
                />
              }
              label="Wait for Completion"
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Whether to wait for the search to complete before continuing
            </Typography>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Pagination */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6">Pagination</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box display="flex" gap={2}>
            <TextField
              type="number"
              value={normalizedPayload.limit}
              onChange={(e) =>
                onChange({ ...normalizedPayload, limit: parseInt(e.target.value, 10) || 10 })
              }
              label="Limit"
              helperText="Maximum number of results"
              inputProps={{ min: 1, max: 100 }}
              sx={{ flex: 1 }}
            />

            <TextField
              type="number"
              value={normalizedPayload.offset}
              onChange={(e) =>
                onChange({ ...normalizedPayload, offset: parseInt(e.target.value, 10) || 0 })
              }
              label="Offset"
              helperText="Number of results to skip"
              inputProps={{ min: 0 }}
              sx={{ flex: 1 }}
            />
          </Box>
        </AccordionDetails>
      </Accordion>

      <Alert severity="success" sx={{ mt: 2 }}>
        <Typography variant="body2">
          <strong>Examples:</strong>
          <br />• Search for education nonprofits: searchTerm=&quot;education&quot;,
          causes=[&quot;education&quot;]
          <br />• Find US nonprofits accepting donations: countryCode=&quot;US&quot;,
          acceptsDonations=true
          <br />• Limit results: limit=5, offset=0
        </Typography>
      </Alert>
    </Box>
  );
};
